import {
  UnorderedList,
  ListItem,
  Text,
  Tooltip,
  Flex,
  Skeleton,
} from '@wegroup/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toEuro } from '../../../utils/stringUtils';
import { mq } from '../../../../../common/src/utils/styledComponentsUtils';

// Components
import LabelValue from '../../offers/LabelValue';

// Types
import {
  isOfferFailed,
  isOfferSuccessfull,
  LouiseOffer,
} from '../../../../../../types/Quotes';
import { getGuaranteeNameForKey } from '../../../services/productFactoryService';
import TooltipCircle from '../../../../../common/src/components/TooltipCircle';
import QuoteErrorMessage from '../../default/QuoteErrorMessage';
import { handleResponseLanguage } from '../../../../../../i18n';
import OfferReportModal from '../../offers/OfferReportModal';
import OfferActionLink from '../../OfferActionLink';

interface Props {
  offer: LouiseOffer;
}

const SingleSelectedOfferPrice: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
}) => {
  const { t } = useTranslation();

  // no useBoolean because modal expects setState
  const [showReportModal, setShowReportModal] = useState<boolean>(false);

  if (isOfferFailed(offer))
    return (
      <>
        <OfferReportModal
          showReportModal={showReportModal}
          setShowReportModal={setShowReportModal}
          offer={offer}
        />
        <QuoteErrorMessage
          httpCode={offer.code}
          message={handleResponseLanguage(offer.errorMessage)}
        />
        <OfferActionLink onClick={() => setShowReportModal(true)}>
          {t('Report error with this offer')}
        </OfferActionLink>
      </>
    );

  return (
    <StyledLabelValue
      label={
        <Flex align="center" minH="7">
          <Text mr="2">{t('Total')}</Text>
          {isOfferSuccessfull(offer) && (
            <Tooltip
              label={
                <>
                  <Text fontWeight="bold" mb={2}>
                    {t('Guarantees')}:
                  </Text>
                  <UnorderedList>
                    {offer.guarantees.map(({ name, total_premium }) => (
                      <ListItem key={`price_guarantee_${name}`}>
                        <Text whiteSpace="nowrap">
                          {getGuaranteeNameForKey(
                            name,
                            offer.guarantee_content,
                          )}
                          : {toEuro(total_premium)}{' '}
                        </Text>
                      </ListItem>
                    ))}
                  </UnorderedList>
                </>
              }
            >
              <TooltipCircle>?</TooltipCircle>
            </Tooltip>
          )}
        </Flex>
      }
    >
      <Total>
        {offer.premium?.total_premium ? (
          toEuro(offer.premium?.total_premium)
        ) : (
          <Skeleton h="10" w="80%" />
        )}
      </Total>
    </StyledLabelValue>
  );
};

const StyledLabelValue = styled(LabelValue)`
  display: none;
  ${mq.laptop} {
    display: block;
  }
`;

const Total = styled.p`
  font-size: 3.6rem;
  line-height: 125%;
  min-height: 48px;
  margin-bottom: 8px;
`;

export default SingleSelectedOfferPrice;
